import { useState, useRef, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';
import Sidebar from './Sidebar';

function LogoutIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
      <polyline points="16 17 21 12 16 7"/>
      <line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  );
}

function titleFor(pathname) {
  const map = [
    ['/dashboard',                   'Pilotage',               'Tableau de bord'],
    ['/eleves',                      'Scolarité',              'Élèves'],
    ['/etablissement/admissions',    'Scolarité',              'Admissions & réinscriptions'],
    ['/presences/rapports',          'Scolarité',              "Rapport d'absences"],
    ['/presences',                   'Scolarité',              'Présences élèves'],
    ['/support/vie-scolaire',        'Scolarité',              'Vie scolaire'],
    ['/support/sante',               'Scolarité',              'Santé'],
    ['/support/formulaires',         'Scolarité',              'Formulaires administratifs'],
    ['/pedagogie/bulletins',         'Pédagogie',              'Bulletins'],
    ['/bulletins',                   'Pédagogie',              'Bulletin'],
    ['/pedagogie/validation-notes',  'Pédagogie',              'Validation des notes'],
    ['/pedagogie/classements',       'Pédagogie',              'Classements & moyennes'],
    ['/pedagogie/rapports',          'Pédagogie',              'Rapports pédagogiques'],
    ['/pedagogie/cahier-texte',      'Pédagogie',              'Cahier de texte'],
    ['/evaluations',                 'Pédagogie & Évaluations','Évaluations'],
    ['/emploi-du-temps',             'Emploi du temps',        'Grille horaire'],
    ['/finance/tresorerie',          'Finance',                'Trésorerie'],
    ['/finance/paiements',           'Finance',                'Gestion des paiements'],
    ['/finance',                     'Finance',                'Recouvrement'],
    ['/communication/annonces',      'Communication',          'Annonces & actualités'],
    ['/communication/personnel',     'Ressources humaines',    'Personnel'],
    ['/portail-enseignant',          'Ressources humaines',    'Portail enseignant'],
    ['/communication/contrats',      'Ressources humaines',    'Contrats'],
    ['/communication/conges',        'Ressources humaines',    'Congés & absences'],
    ['/communication',               'Communication',          'Messagerie & SMS'],
    ['/bibliotheque',                'Bibliothèque',           'Catalogue'],
    ['/etablissement/structure',     'Établissement',          'Structure académique'],
    ['/etablissement',               'Établissement',          'Fiche établissement'],
    ['/admin/roles',                 'Administration',         'Gestion des rôles'],
    ['/parent',                      'Mon espace',             'Espace parent'],
    ['/plateforme/ecoles',           'Plateforme',             'Écoles clientes'],
    ['/plateforme/plans',            'Plateforme',             'Plans d\'abonnement'],
    ['/plateforme/abonnements',      'Plateforme',             'Abonnements'],
    ['/plateforme/facturation',      'Plateforme',             'Facturation'],
    ['/plateforme/support',          'Plateforme',             'Support technique'],
    ['/plateforme/supervision',      'Plateforme',             'Supervision'],
    ['/plateforme/securite',         'Plateforme',             'Sécurité'],
    ['/plateforme/configuration',    'Plateforme',             'Configuration'],
    ['/plateforme/marketing',        'Plateforme',             'Marketing'],
    ['/plateforme/analytique',       'Plateforme',             'Analytique'],
    ['/plateforme',                  'Plateforme',             'Administration'],
  ];
  for (const [prefix, eyebrow, title] of map) {
    if (pathname.startsWith(prefix)) return [eyebrow, title];
  }
  return ['Schoolizy', 'Console'];
}

const DASHBOARD_SHORTCUTS = [
  '/eleves', '/presences', '/evaluations', '/finance',
  '/bibliotheque', '/communication', '/support', '/pedagogie',
  '/emploi-du-temps', '/portail-enseignant', '/admin',
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const menuRef = useRef(null);

  const initials = ((user?.firstName?.[0] || '') + (user?.lastName?.[0] || '')).toUpperCase() || 'U';
  const ROLE_PRIORITY = ['SUPER_ADMIN','ADMINISTRATEUR','PROVISEUR','CENSEUR','SECRETAIRE','COMPTABLE','SURVEILLANT','ENSEIGNANT','PARENT'];
  const roleName = (ROLE_PRIORITY.find(r => (user?.roles || []).includes(r)) || user?.roles?.[0] || '').replace(/_/g, ' ');

  function handleLogout() { logout(); navigate('/login'); }
  const [eyebrow, title] = titleFor(pathname);
  const showBack = DASHBOARD_SHORTCUTS.some((p) => pathname.startsWith(p)) && pathname !== '/dashboard';

  useEffect(() => {
    function close(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  function openModal() {
    setMenuOpen(false);
    setForm({ currentPassword: '', newPassword: '', confirm: '' });
    setFormError(''); setFormSuccess('');
    setModal(true);
  }

  async function submitChange(e) {
    e.preventDefault();
    if (!form.currentPassword || !form.newPassword) { setFormError('Remplissez tous les champs.'); return; }
    if (form.newPassword !== form.confirm) { setFormError('Les mots de passe ne correspondent pas.'); return; }
    if (form.newPassword.length < 6) { setFormError('Minimum 6 caractères.'); return; }
    setSaving(true); setFormError('');
    try {
      await api.patch('/auth/change-password', { currentPassword: form.currentPassword, newPassword: form.newPassword });
      setFormSuccess('Mot de passe modifié avec succès.');
      setTimeout(() => setModal(false), 1500);
    } catch (err) {
      setFormError(err.message || 'Erreur lors du changement.');
    } finally { setSaving(false); }
  }

  return (
    <>
      <div className="shell">
        <Sidebar />
        <div className="main">
          <header className="topbar">
            <div className="topbar-left">
              {showBack && (
                <button className="topbar-back" onClick={() => navigate('/dashboard')}>
                  ← Tableau de bord
                </button>
              )}
              <div>
                <div className="topbar-eyebrow">{eyebrow}</div>
                <div className="topbar-title">{title}</div>
              </div>
            </div>
            <div className="topbar-right">
              <div ref={menuRef} style={{ position: 'relative' }}>
                <div className="topbar-profile" onClick={() => setMenuOpen(o => !o)}
                  style={{ cursor: 'pointer', userSelect: 'none' }}>
                  <div className="topbar-avatar">{initials}</div>
                  <div className="topbar-profile-info">
                    <span className="topbar-profile-name">{user?.firstName} {user?.lastName}</span>
                    <span className="topbar-profile-role">{roleName}</span>
                  </div>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                    style={{ marginLeft: 4, color: 'var(--muted)', flexShrink: 0 }}>
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </div>
                {menuOpen && (
                  <div style={{
                    position: 'absolute', top: '100%', right: 0, marginTop: 6,
                    background: 'var(--surface)', border: '1px solid var(--line)',
                    borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,.1)',
                    minWidth: 200, zIndex: 999, overflow: 'hidden',
                  }}>
                    <button onClick={openModal} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      width: '100%', padding: '11px 16px', background: 'none',
                      border: 'none', cursor: 'pointer', fontSize: 13,
                      color: 'var(--text)', textAlign: 'left',
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                      Changer le mot de passe
                    </button>
                    <div style={{ height: 1, background: 'var(--line)' }} />
                    <button onClick={handleLogout} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      width: '100%', padding: '11px 16px', background: 'none',
                      border: 'none', cursor: 'pointer', fontSize: 13,
                      color: '#ef4444', textAlign: 'left',
                    }}>
                      <LogoutIcon />
                      Se déconnecter
                    </button>
                  </div>
                )}
              </div>
            </div>
          </header>
          <main className="content">
            <Outlet />
          </main>
        </div>
      </div>

      {modal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <h2 style={{ marginBottom: 16, fontSize: '1rem' }}>Changer le mot de passe</h2>
            {formError   && <div className="form-error">{formError}</div>}
            {formSuccess && <div style={{ background: '#dcfce7', color: '#166534', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13 }}>{formSuccess}</div>}
            <form onSubmit={submitChange}>
              <div className="field">
                <label>Mot de passe actuel</label>
                <input type="password" value={form.currentPassword} autoFocus
                  onChange={e => setForm(p => ({ ...p, currentPassword: e.target.value }))} />
              </div>
              <div className="field">
                <label>Nouveau mot de passe</label>
                <input type="password" value={form.newPassword}
                  onChange={e => setForm(p => ({ ...p, newPassword: e.target.value }))} />
              </div>
              <div className="field">
                <label>Confirmer</label>
                <input type="password" value={form.confirm}
                  onChange={e => setForm(p => ({ ...p, confirm: e.target.value }))} />
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
                <button type="button" className="btn" onClick={() => setModal(false)}>Annuler</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
